import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import BillingCard from '../../components/BillingCard';
import StatusMessage from '../../components/StatusMessage';

export default function BillingPage() {
  const [params, setParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [notice] = useState(() => billingNotice(params.get('billing')));
  // Stripe activates the subscription on its own webhook, which lands *after*
  // it redirects the customer back here, so the plan has to be re-read a few
  // seconds later as well as immediately.
  //
  // Those later reads deliberately aren't cancelled by this effect's cleanup.
  // Scrubbing the result out of the URL below changes `params` (and with it
  // react-router's `setParams` identity), so the effect re-runs and a cleanup
  // would tear down the very timers it had just set — leaving the page on the
  // pre-checkout plan until a manual reload. Leaving the page is handled by a
  // flag the scheduled reads check instead, so there is no cleanup to get the
  // ordering wrong.
  const live = useRef(true);
  const handled = useRef(false);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  useEffect(() => {
    if (handled.current) return;
    const result = params.get('billing');
    if (!result) return;
    handled.current = true;
    setParams({}, { replace: true });
    if (result !== 'success') return;
    const reread = () => {
      if (live.current) void refreshUser();
    };
    reread();
    window.setTimeout(reread, 2500);
    window.setTimeout(reread, 6000);
  }, [params, refreshUser, setParams]);

  return <div className="account-page-content"><div className="page-heading"><p className="eyebrow">Subscription</p><h1>Billing</h1><p>Review your plan and manage payment securely through Stripe.</p></div>{notice && <StatusMessage kind="info">{notice}</StatusMessage>}<BillingCard /></div>;
}

function billingNotice(result: string | null): string {
  if (result === 'success') return 'Payment received — activating your subscription…';
  if (result === 'cancelled') return 'Checkout cancelled — no changes were made.';
  return '';
}
